import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import MenuBar from "../components/MenuBar";
import { startLoading, stopLoading, setTaskMessage, LOADING_TYPES } from "../loading";
import { canAccessSalesDataImport } from "../features/salesDataImport/access";
import SalesImportNav from "../features/salesDataImport/components/SalesImportNav";
import {
  SALES_IMPORT_BRANCH_OPTIONS,
  salesImportBranchOutwardSupplyLabel,
} from "../features/salesDataImport/config/salesImportBranchIds";
import { RECORDS_PAGE_SIZE } from "../features/salesDataImport/constants";
import { fetchDiscountSalesReport } from "../features/salesDataImport/services/discountSalesReportService";
import {
  exportDiscountSalesReportExcel,
  exportDiscountSalesReportPdf,
} from "../features/salesDataImport/services/discountSalesReportExportService";
import { formatFirebaseError } from "../features/salesDataImport/utils/firebaseError";
import "./SalesDataImportCenter.css";
import "./DiscountSalesReportPage.css";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function fm(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function pct(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return `${Number(v).toFixed(1)}%`;
}

function defaultFilters() {
  const now = new Date();
  return {
    year: now.getFullYear(),
    monthIndex: now.getMonth(),
    branchId: SALES_IMPORT_BRANCH_OPTIONS[0] || "1",
    employeeName: "all",
  };
}

export default function DiscountSalesReportPage() {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}") || {};
    } catch {
      return {};
    }
  }, []);

  const [dark, setDark] = useState(() => localStorage.getItem("sdic-dark") === "1");
  const [draft, setDraft] = useState(defaultFilters);
  const [applied, setApplied] = useState(null);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [showDiscountBreakdown, setShowDiscountBreakdown] = useState(false);
  const [selectedDiscountRange, setSelectedDiscountRange] = useState(null);
  const [rangeItemPage, setRangeItemPage] = useState(0);

  const selectDiscountRange = useCallback((label) => {
    setSelectedDiscountRange((prev) => (prev === label ? null : label));
    setRangeItemPage(0);
  }, []);

  useEffect(() => {
    if (!selectedDiscountRange) return;
    const el = document.getElementById("dsr-range-invoices");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [selectedDiscountRange]);

  const rangeItems = useMemo(() => {
    if (!report?.charts?.discountBreakdown || !selectedDiscountRange) return [];
    const bucket = report.charts.discountBreakdown.find((b) => b.label === selectedDiscountRange);
    return bucket?.items || bucket?.invoices || [];
  }, [report, selectedDiscountRange]);

  const pagedRangeItems = useMemo(() => {
    const start = rangeItemPage * RECORDS_PAGE_SIZE;
    return rangeItems.slice(start, start + RECORDS_PAGE_SIZE);
  }, [rangeItems, rangeItemPage]);

  const openDiscountBreakdown = useCallback(() => {
    if (!report?.charts?.discountBreakdown?.length) {
      toast.error("No discounted line items in this period.");
      return;
    }
    setShowDiscountBreakdown(true);
    requestAnimationFrame(() => {
      document.getElementById("dsr-discount-breakdown")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [report]);

  const onPieSegmentClick = useCallback(
    (entry) => {
      if (entry?.name === "Discount Sales") openDiscountBreakdown();
    },
    [openDiscountBreakdown]
  );

  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return [y - 2, y - 1, y, y + 1];
  }, []);

  const loadReport = useCallback(async (filters) => {
    if (!filters?.branchId) {
      toast.error("Select a branch.");
      return;
    }

    setLoading(true);
    const taskId = startLoading({
      type: LOADING_TYPES.REPORT,
      report: true,
      message: "Generating discount sales report…",
    });

    try {
      const out = await fetchDiscountSalesReport({
        ...filters,
        onProgress: (rows, chunks) => {
          setTaskMessage(
            taskId,
            chunks
              ? `Loaded ${rows.toLocaleString("en-IN")} lines (${chunks} chunks)…`
              : `Loaded ${rows.toLocaleString("en-IN")} lines…`
          );
        },
      });
      setReport(out);
      setShowDiscountBreakdown(false);
      setSelectedDiscountRange(null);
      setRangeItemPage(0);
      if (!out.meta.itemCount) {
        toast.error("No Outward Supply line items found for this month and branch.");
      } else {
        toast.success(
          `${out.meta.itemCount.toLocaleString()} item line(s) · ${out.meta.invoiceCount.toLocaleString()} invoice(s)`
        );
      }
    } catch (e) {
      toast.error(formatFirebaseError(e));
      setReport(null);
    } finally {
      stopLoading(taskId);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!applied) return;
    loadReport(applied);
  }, [applied, loadReport]);

  useEffect(() => {
    localStorage.setItem("sdic-dark", dark ? "1" : "0");
  }, [dark]);

  const handleSearch = () => {
    if (!draft.branchId) {
      toast.error("Branch is required.");
      return;
    }
    setApplied({ ...draft });
  };

  const handleReset = () => {
    setDraft(defaultFilters());
    setApplied(null);
    setReport(null);
    setShowDiscountBreakdown(false);
    setSelectedDiscountRange(null);
    setRangeItemPage(0);
  };

  const employeeOptions = report?.employeeOptions || [];

  if (!canAccessSalesDataImport(user)) {
    return <div className="p-6 text-center">Access denied — Admin or Storekeeper only.</div>;
  }

  return (
    <div className={`sdic-root dsr-root${dark ? " sdic-dark" : ""}`}>
      <MenuBar />
      <header className="sdic-header dsr-no-print">
        <div>
          <h1>Discount vs Non-Discount Sales Report</h1>
          <p>Item-wise discount report — branch maps to Outward Supply voucher type</p>
        </div>
        <div className="dsr-header-actions">
          {report && (
            <>
              <button
                type="button"
                className="sdic-btn sdic-btn-secondary"
                onClick={() => exportDiscountSalesReportExcel(report)}
              >
                Excel
              </button>
              <button
                type="button"
                className="sdic-btn sdic-btn-secondary"
                onClick={() => exportDiscountSalesReportPdf(report)}
              >
                PDF
              </button>
              <button type="button" className="sdic-btn sdic-btn-secondary" onClick={() => window.print()}>
                Print
              </button>
            </>
          )}
          <button type="button" className="sdic-btn sdic-btn-secondary" onClick={() => setDark((d) => !d)}>
            {dark ? "Light" : "Dark"}
          </button>
        </div>
      </header>

      <div className="sdic-body">
        <SalesImportNav active="discount-sales" showBranchFilter={false} />

        <section className="sdic-panel dsr-no-print">
          <h2 className="sdic-panel-title">Filters</h2>
          <div className="dsr-filters">
            <label>
              Month <span className="dsr-req">*</span>
              <div className="dsr-month-row">
                <select
                  value={draft.monthIndex}
                  onChange={(e) => setDraft((d) => ({ ...d, monthIndex: Number(e.target.value) }))}
                >
                  {MONTH_NAMES.map((name, i) => (
                    <option key={name} value={i}>
                      {name}
                    </option>
                  ))}
                </select>
                <select
                  value={draft.year}
                  onChange={(e) => setDraft((d) => ({ ...d, year: Number(e.target.value) }))}
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </label>

            <label>
              Branch <span className="dsr-req">*</span>
              <select
                value={draft.branchId}
                onChange={(e) => setDraft((d) => ({ ...d, branchId: e.target.value }))}
              >
                {SALES_IMPORT_BRANCH_OPTIONS.map((id) => (
                  <option key={id} value={id}>
                    {salesImportBranchOutwardSupplyLabel(id)}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Employee
              <select
                value={draft.employeeName}
                onChange={(e) => setDraft((d) => ({ ...d, employeeName: e.target.value }))}
              >
                <option value="all">All employees</option>
                {employeeOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>

            <div className="dsr-filter-actions">
              <button type="button" className="sdic-btn sdic-btn-primary" onClick={handleSearch} disabled={loading}>
                {loading ? "Searching…" : "Search"}
              </button>
              <button type="button" className="sdic-btn sdic-btn-secondary" onClick={handleReset} disabled={loading}>
                Reset
              </button>
            </div>
          </div>
        </section>

        {report && (
          <>
            <div className="dsr-print-only dsr-print-header">
              <h1>Discount vs Non-Discount Sales Report</h1>
              <p>
                {report.meta.monthLabel} · {report.meta.branchLabel}
                {report.meta.employeeName !== "all" ? ` · ${report.meta.employeeName}` : ""}
                {" · "}
                {report.meta.fromDate} → {report.meta.toDate}
              </p>
            </div>

            <section className="sdic-cards dsr-cards">
              <div className="sdic-card dsr-card">
                <span className="dsr-card-label">Total Outward Supply Sales</span>
                <strong className="dsr-card-value">₹ {fm(report.cards.totalOutwardSales.amount)}</strong>
                <span className="dsr-card-sub">
                  {report.cards.totalOutwardSales.lineCount} items · {report.cards.totalOutwardSales.invoiceCount} invoices
                </span>
              </div>
              <div className="sdic-card dsr-card">
                <span className="dsr-card-label">Non-Discount Sales</span>
                <strong className="dsr-card-value">₹ {fm(report.cards.nonDiscountSales.amount)}</strong>
                <span className="dsr-card-sub">
                  {report.cards.nonDiscountSales.lineCount} items · Employee Name &quot;an Employee&quot;, discount 0
                </span>
              </div>
              <div className="sdic-card dsr-card">
                <span className="dsr-card-label">Commission Sales</span>
                <strong className="dsr-card-value">₹ {fm(report.cards.employeeSales.amount)}</strong>
                <span className="dsr-card-sub">
                  {report.cards.employeeSales.lineCount} items · {report.cards.employeeSales.invoiceCount} invoices
                </span>
              </div>
              <button
                type="button"
                className="sdic-card dsr-card dsr-card-clickable"
                onClick={openDiscountBreakdown}
                title="Click to view discount amount breakdown by range"
              >
                <span className="dsr-card-label">Discounted Sales</span>
                <strong className="dsr-card-value">₹ {fm(report.cards.discountedSales.amount)}</strong>
                <span className="dsr-card-sub">
                  {report.cards.discountedSales.lineCount} items · ₹ {fm(report.cards.discountedSales.discountAmount)} discount
                </span>
                <span className="dsr-card-hint dsr-no-print">Click for discount % breakdown</span>
              </button>
              <div className="sdic-card dsr-card dsr-card-accent">
                <span className="dsr-card-label">Discount % of Sales</span>
                <strong className="dsr-card-value">{pct(report.cards.discountPercentage)}</strong>
                <span className="dsr-card-sub">Discounted amount ÷ total sales</span>
              </div>
            </section>

            <section className="dsr-charts">
              <div className="sdic-panel dsr-chart-panel dsr-chart-panel--pie">
                <h3>Non-Discount · Discount · Commission sales</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={report.charts.pie}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={85}
                      onClick={onPieSegmentClick}
                      style={{ cursor: "pointer" }}
                      label={({ name, percent }) =>
                        percent >= 0.04 ? `${name.split(" (")[0]} ${(percent * 100).toFixed(0)}%` : ""
                      }
                    >
                      {report.charts.pie.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={entry.color}
                          stroke={entry.name === "Discount Sales" && showDiscountBreakdown ? "#fff" : undefined}
                          strokeWidth={entry.name === "Discount Sales" && showDiscountBreakdown ? 2 : 0}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v, _name, props) => [
                        `₹ ${fm(v)}${props?.payload?.lineCount != null ? ` · ${props.payload.lineCount} items` : ""}`,
                        props?.payload?.name,
                      ]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
                <p className="dsr-chart-hint dsr-no-print">Click <strong>Discount Sales</strong> slice or the Discounted Sales card above</p>
              </div>

              <div className="sdic-panel dsr-chart-panel dsr-no-print">
                <h3>Commission Sales by Employee</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={report.charts.employeeBar} margin={{ left: 8, right: 8, bottom: 48 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="employee" angle={-35} textAnchor="end" interval={0} height={70} tick={{ fontSize: 10 }} />
                    <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => fm(v)} />
                    <Bar dataKey="amount" fill="#5d0081" name="Commission Sales" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>

            {showDiscountBreakdown && report.charts.discountBreakdown?.length > 0 && (
              <section id="dsr-discount-breakdown" className="sdic-panel dsr-breakdown">
                <div className="dsr-breakdown-header">
                  <div>
                    <h2 className="sdic-panel-title">Discount amount by discount % range</h2>
                    <p className="dsr-breakdown-desc">
                      Discounted line items grouped by item discount % — click a range to list its items.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="sdic-btn sdic-btn-secondary dsr-no-print"
                    onClick={() => {
                      setShowDiscountBreakdown(false);
                      setSelectedDiscountRange(null);
                      setRangeItemPage(0);
                    }}
                  >
                    Close
                  </button>
                </div>

                <div className="dsr-breakdown-chart dsr-no-print">
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={report.charts.discountBreakdown} margin={{ left: 8, right: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis tickFormatter={(v) => fm(v)} width={72} />
                      <Tooltip
                        formatter={(v, name) => [
                          name === "discountAmount" ? `₹ ${fm(v)}` : fm(v),
                          name === "discountAmount" ? "Discount amount" : name === "netSales" ? "Net sales" : name,
                        ]}
                      />
                      <Legend />
                      <Bar
                        dataKey="discountAmount"
                        fill="#9333ea"
                        name="Discount amount (₹)"
                        radius={[4, 4, 0, 0]}
                        cursor="pointer"
                        onClick={(row) => {
                          if (row?.label) selectDiscountRange(row.label);
                        }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="sdic-table-wrap">
                  <table className="sdic-table">
                    <thead>
                      <tr>
                        <th>Discount % range</th>
                        <th>Items</th>
                        <th>Net sales</th>
                        <th>Discount amount</th>
                        <th>Avg discount %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.charts.discountBreakdown.map((row) => (
                        <tr
                          key={row.label}
                          className={`dsr-breakdown-row${selectedDiscountRange === row.label ? " dsr-breakdown-row--active" : ""}`}
                          onClick={() => selectDiscountRange(row.label)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              selectDiscountRange(row.label);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          title="Click to show line items in this range"
                        >
                          <td>{row.label}</td>
                          <td className="dsr-num">{row.lineCount ?? row.invoiceCount}</td>
                          <td className="dsr-num">₹ {fm(row.netSales)}</td>
                          <td className="dsr-num">₹ {fm(row.discountAmount)}</td>
                          <td className="dsr-num">
                            {row.netSales + row.discountAmount > 0
                              ? pct((row.discountAmount / (row.netSales + row.discountAmount)) * 100)
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="dsr-breakdown-total">
                        <td>Total</td>
                        <td className="dsr-num">
                          {report.charts.discountBreakdown.reduce((s, r) => s + (r.lineCount ?? r.invoiceCount), 0)}
                        </td>
                        <td className="dsr-num">
                          ₹ {fm(report.charts.discountBreakdown.reduce((s, r) => s + r.netSales, 0))}
                        </td>
                        <td className="dsr-num">
                          ₹ {fm(report.charts.discountBreakdown.reduce((s, r) => s + r.discountAmount, 0))}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {selectedDiscountRange && (
                  <div id="dsr-range-invoices" className="dsr-range-invoices dsr-no-print">
                    <div className="dsr-range-invoices-header">
                      <h3>
                        Line items — discount {selectedDiscountRange}
                        <span className="dsr-range-count">({rangeItems.length.toLocaleString()})</span>
                      </h3>
                      <button
                        type="button"
                        className="sdic-btn sdic-btn-secondary"
                        onClick={() => {
                          setSelectedDiscountRange(null);
                          setRangeItemPage(0);
                        }}
                      >
                        Clear selection
                      </button>
                    </div>
                    <div className="sdic-table-wrap dsr-table-wrap">
                      <table className="sdic-table dsr-table">
                        <thead>
                          <tr>
                            <th>Invoice No</th>
                            <th>Date</th>
                            <th>Item</th>
                            <th>Customer</th>
                            <th>Employee</th>
                            <th>Gross</th>
                            <th>Discount</th>
                            <th>Net</th>
                            <th>Disc %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedRangeItems.map((line) => (
                            <tr key={line.lineKey || `${line.invoiceNo}-${line.invoiceDate}-${line.itemName}-${line.netAmount}`}>
                              <td>{line.invoiceNo}</td>
                              <td>{line.invoiceDate || "—"}</td>
                              <td>{line.itemName || "—"}</td>
                              <td>{line.customerName || "—"}</td>
                              <td>{line.employee || "—"}</td>
                              <td className="dsr-num">₹ {fm(line.grossAmount)}</td>
                              <td className="dsr-num">₹ {fm(line.discountAmount)}</td>
                              <td className="dsr-num">₹ {fm(line.netAmount)}</td>
                              <td className="dsr-num">{pct(line.discountPct)}</td>
                            </tr>
                          ))}
                          {!pagedRangeItems.length && (
                            <tr>
                              <td colSpan={9} style={{ textAlign: "center" }}>
                                No line items in this range.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {rangeItems.length > RECORDS_PAGE_SIZE && (
                      <div className="dsr-pagination">
                        <button
                          type="button"
                          className="sdic-btn sdic-btn-secondary"
                          disabled={rangeItemPage === 0}
                          onClick={() => setRangeItemPage((p) => p - 1)}
                        >
                          Previous
                        </button>
                        <span>
                          Page {rangeItemPage + 1} of {Math.ceil(rangeItems.length / RECORDS_PAGE_SIZE)}
                        </span>
                        <button
                          type="button"
                          className="sdic-btn sdic-btn-secondary"
                          disabled={(rangeItemPage + 1) * RECORDS_PAGE_SIZE >= rangeItems.length}
                          onClick={() => setRangeItemPage((p) => p + 1)}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </section>
            )}

            <section className="sdic-panel">
              <h2 className="sdic-panel-title">Commission Sales ranking</h2>
              <div className="sdic-table-wrap">
                <table className="sdic-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Employee</th>
                      <th>Sales Amount</th>
                      <th>Item Count</th>
                      <th>Avg Line</th>
                      <th>Contribution %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.employeeRanking.map((r, idx) => (
                      <tr key={r.employee} className={idx >= 15 ? "dsr-print-hide" : undefined}>
                        <td>{r.rank}</td>
                        <td>{r.employee}</td>
                        <td className="dsr-num">₹ {fm(r.salesAmount)}</td>
                        <td className="dsr-num">{r.lineCount ?? r.invoiceCount}</td>
                        <td className="dsr-num">₹ {fm(r.averageLine ?? r.averageBill)}</td>
                        <td className="dsr-num">{pct(r.contributionPct)}</td>
                      </tr>
                    ))}
                    {!report.employeeRanking.length && (
                      <tr>
                        <td colSpan={6} style={{ textAlign: "center" }}>
                          No commission sales in this period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="sdic-panel dsr-summary dsr-no-print">
              <h2 className="sdic-panel-title">Summary</h2>
              <dl className="dsr-summary-grid">
                <div><dt>Total Outward Supply Amount</dt><dd>₹ {fm(report.summary.totalOutwardAmount)}</dd></div>
                <div><dt>Total Non-Discount Sales</dt><dd>₹ {fm(report.summary.totalNonDiscountSales)}</dd></div>
                <div><dt>Total Commission Sales</dt><dd>₹ {fm(report.summary.totalEmployeeSales)}</dd></div>
                <div><dt>Total Discounted Sales</dt><dd>₹ {fm(report.summary.totalDiscountedSales)}</dd></div>
                <div><dt>Total Discount Given</dt><dd>₹ {fm(report.summary.totalDiscountGiven)}</dd></div>
                <div><dt>Average Discount %</dt><dd>{pct(report.summary.averageDiscountPct)}</dd></div>
                <div>
                  <dt>Highest Discount Line</dt>
                  <dd>
                    {report.summary.highestDiscountLine
                      ? `${report.summary.highestDiscountLine.invoiceNo} · ${report.summary.highestDiscountLine.itemName} (${pct(report.summary.highestDiscountLine.discountPct)})`
                      : report.summary.highestDiscountInvoice
                        ? `${report.summary.highestDiscountInvoice.invoiceNo} (${pct(report.summary.highestDiscountInvoice.discountPct)})`
                        : "—"}
                  </dd>
                </div>
                <div>
                  <dt>Lowest Discount Line</dt>
                  <dd>
                    {report.summary.lowestDiscountLine
                      ? `${report.summary.lowestDiscountLine.invoiceNo} · ${report.summary.lowestDiscountLine.itemName} (${pct(report.summary.lowestDiscountLine.discountPct)})`
                      : report.summary.lowestDiscountInvoice
                        ? `${report.summary.lowestDiscountInvoice.invoiceNo} (${pct(report.summary.lowestDiscountInvoice.discountPct)})`
                        : "—"}
                  </dd>
                </div>
              </dl>
              <p className="dsr-meta">
                {report.meta.monthLabel} · {report.meta.branchLabel}
                {report.meta.employeeName !== "all" ? ` · ${report.meta.employeeName}` : ""}
                {" · "}
                {report.meta.fromDate} → {report.meta.toDate}
              </p>
            </section>
          </>
        )}

        {!report && !loading && (
          <section className="sdic-panel dsr-empty">
            <p>Select month and branch, then click <strong>Search</strong> to generate the report.</p>
          </section>
        )}
      </div>
    </div>
  );
}
